/**
 * Column primitives shared by every table validator, plus the shapes of the JSON columns.
 * Messages come from ui.id.json (CONTENT.md section 16) so forms, the API and import say
 * the same thing.
 *
 * JSON columns are validated as their parsed shape; the storage layer serialises them to
 * text on the way into SQLite or Postgres and parses them on the way out.
 */
import { z } from 'zod'
import {
  isFuturePartialDate,
  isPartialDate,
  parsePartialDateInput,
  tanggalTerlaluJauh,
} from '../dates/partial'
import {
  JENIS_BAPTIS,
  JENIS_PERKAWINAN_LUAR,
  JENIS_SURAT,
  TABEL_REGISTER,
} from '../schema/contract'
import ui from '../strings/ui.id.json' with { type: 'json' }
import { hlcDecode } from '../sync/hlc'

const pesan = ui.validasi

export const uuidSchema = z.uuid()
export const timestampSchema = z.iso.datetime({ offset: true })
export const partialDateSchema = z.string().refine(isPartialDate, pesan.tanggalParsial)
export const requiredTextSchema = z.string().trim().min(1, pesan.wajib)
export const optionalTextSchema = z.string().trim().nullish()
export const nikSchema = z.string().regex(/^\d{16}$/, pesan.nik)
export const phoneSchema = z.string().regex(/^(08\d{7,11}|\+62\d{8,12})$/, pesan.telepon)
export const emailSchema = z.email(pesan.email)
export const positiveIntSchema = z.number().int().positive()
export const nonNegativeIntSchema = z.number().int().nonnegative()
/** `umat.anak_ke` (schema 15): the person's place among the parents' children, 1 to 30. */
export const ANAK_KE_MAKS = 30
export const anakKeSchema = z
  .number()
  .int(pesan.anakKe)
  .min(1, pesan.anakKe)
  .max(ANAK_KE_MAKS, pesan.anakKe)
/**
 * `umat.tahun_lulus` (schema 15): a year of four digits, the one expected allowed (a pupil's);
 * bounded by the calendar, never by today, so the device and the push agree on every day.
 */
export const TAHUN_LULUS_MIN = 1900
export const TAHUN_LULUS_MAKS = 2100
export const tahunLulusSchema = z
  .number()
  .int(pesan.tahunLulus)
  .min(TAHUN_LULUS_MIN, pesan.tahunLulus)
  .max(TAHUN_LULUS_MAKS, pesan.tahunLulus)
/**
 * A latitude or a longitude in degrees (schema 16, Peta umat): a finite number within the globe.
 * Whether it lies in Indonesia is a warning on the device (`diIndonesia`, ../peta), never a
 * refusal: a pin is a person's to place.
 */
export const latSchema = z.number().finite().min(-90, pesan.koordinat).max(90, pesan.koordinat)
export const lngSchema = z.number().finite().min(-180, pesan.koordinat).max(180, pesan.koordinat)
/** A GPS reading's accuracy in whole metres (schema 16): 0 to 100 km. */
export const akurasiMeterSchema = z.number().int().min(0).max(100_000)
/**
 * The key of the address a location answers (schema 16, review I1): 14 lowercase hex digits
 * (../peta/alamat `kunciAlamat`), never the address text.
 */
export const kunciAlamatSchema = z.string().regex(/^[0-9a-f]{14}$/)
/** Crockford base32: digits and letters without I, L, O and U (spec 12.3). */
export const kodeVerifikasiSchema = z
  .string()
  .regex(/^[0-9A-HJKMNP-TV-Z]{8}$/, pesan.kodeVerifikasi)

/**
 * A partial date as a person types it, day first ("12-05-1987", "05-1987", "1987"; the
 * canonical form is accepted too): empty becomes null, anything readable becomes the
 * canonical text, anything else gets the hint of CONTENT section 16. The one date field
 * of every form and of register entry mode (no calendar, no three fields).
 */
export const tanggalKetikSchema = z
  .string()
  .trim()
  .refine((v) => v === '' || parsePartialDateInput(v) !== null, pesan.tanggalParsial)
  .transform((v) => (v === '' ? null : parsePartialDateInput(v)))

/** The same field when the book requires the date. */
export const tanggalKetikWajibSchema = z
  .string()
  .trim()
  .min(1, pesan.wajib)
  .refine((v) => v === '' || parsePartialDateInput(v) !== null, pesan.tanggalParsial)
  .transform((v) => parsePartialDateInput(v) ?? '')

/** A typed date that cannot lie in the future (birth, death; today in WIT). */
export const tanggalLampauKetikSchema = tanggalKetikSchema.refine(
  (v) => v === null || !isFuturePartialDate(v, new Date()),
  pesan.tanggalMasaDepan,
)

/**
 * A typed ceremony date (Baptis, Komuni, Krisma, Perkawinan; release 17): it may lie ahead, the
 * parish preparing the certificate before the ceremony, up to a year (`tanggalTerlaluJauh`,
 * today in WIT); further is refused. The form says a date ahead quietly, without refusing it.
 */
export const tanggalDepanKetikSchema = tanggalKetikSchema.refine(
  (v) => v === null || !tanggalTerlaluJauh(v, new Date()),
  pesan.tanggalTerlaluJauh,
)

export const hlcSchema = z.string().refine((value) => {
  try {
    hlcDecode(value)
    return true
  } catch {
    return false
  }
}, 'HLC')

/** `_hlc`: field name to the HLC of its last write (spec 8.1, 9.3). */
export const hlcMapSchema = z.record(z.string(), hlcSchema)

/** The six columns every synced table carries (spec 8.1). */
export const syncShape = {
  id: uuidSchema,
  paroki_id: uuidSchema.nullable(),
  dibuat_pada: timestampSchema,
  diubah_pada: timestampSchema,
  dihapus_pada: timestampSchema.nullable(),
  _hlc: hlcMapSchema,
}

/** The book reference and minister shared by the five registers (spec 8.4). */
export const registerShape = {
  buku: requiredTextSchema,
  halaman: positiveIntSchema.nullish(),
  nomor: positiveIntSchema,
  tanggal: partialDateSchema.nullish(),
  tempat: optionalTextSchema,
  pelayan: optionalTextSchema,
  pelayan_id: uuidSchema.nullish(),
}

// ---------------------------------------------------------------------------
// JSON column shapes
// ---------------------------------------------------------------------------

export const LANGKAH_PENDAFTARAN = [
  'kk',
  'ktp',
  'hp',
  'email',
  'baptis',
  'komuni',
  'krisma',
  'perkawinan',
  'pendidikan',
  'pekerjaan',
  'anggota',
] as const
export const KEADAAN_LANGKAH = ['selesai', 'belum', 'tidak_ada'] as const

const keadaan = z.enum(KEADAAN_LANGKAH)

/** An answer no column holds: Belum or Tidak tahu (Sudah is a link or a reference). */
export const JAWABAN_TERBUKA = ['belum', 'tidak_tahu'] as const
const jawabanAnggota = z.record(z.string(), z.enum(JAWABAN_TERBUKA))

/**
 * `keluarga.pendaftaran`: the desk-flow state per step (decided 2026-09-28), and since the test
 * sweep of 2026-10-04 (A2) the Belum and Tidak tahu each member answered on the sacrament steps,
 * so a step opened again shows them. JSON keys only; an app before them ignores them.
 */
export const pendaftaranSchema = z.object({
  selesai: z.boolean(),
  langkah: z.object({
    kk: keadaan,
    ktp: keadaan,
    hp: keadaan,
    email: keadaan,
    baptis: keadaan,
    komuni: keadaan,
    krisma: keadaan,
    perkawinan: keadaan,
    pendidikan: keadaan,
    pekerjaan: keadaan,
    anggota: keadaan,
  }),
  jawaban: z
    .object({
      baptis: jawabanAnggota.optional(),
      komuni: jawabanAnggota.optional(),
      krisma: jawabanAnggota.optional(),
    })
    .optional(),
})
export type Pendaftaran = z.infer<typeof pendaftaranSchema>

/** A fresh registration: nothing done yet. */
export function pendaftaranBaru(): Pendaftaran {
  return {
    selesai: false,
    langkah: {
      kk: 'belum',
      ktp: 'belum',
      hp: 'belum',
      email: 'belum',
      baptis: 'belum',
      komuni: 'belum',
      krisma: 'belum',
      perkawinan: 'belum',
      pendidikan: 'belum',
      pekerjaan: 'belum',
      anggota: 'belum',
    },
  }
}

/** One dated marginal annotation; the array is append-only (spec 8.4). */
export const catatanPinggirSchema = z.object({
  tanggal: partialDateSchema,
  teks: requiredTextSchema,
  /** User name, or "Sistem" for app-generated notes. */
  oleh: requiredTextSchema,
})
export const catatanPinggirListSchema = z.array(catatanPinggirSchema)
export type CatatanPinggir = z.infer<typeof catatanPinggirSchema>

/**
 * A sacrament recorded at another parish, copied from the certificate. `pelayan`, `wali`
 * (baptism, confirmation) and `saksi` (marriage) came with Dokumen jadi teks (spec
 * 2026-09-30, 4.4); `nomor_surat` (the certificate's own number), `gereja` (the church),
 * `jenis_baptis` (a baptism's: bayi, dewasa…; never `jenis`, the screens' own discriminator) and `jenis_perkawinan` (a marriage's: Katolik, campur beda
 * gereja, beda agama) with the parish data sheet (release 19): JSON keys only, no column. The
 * parish stays required: a certificate that names only a church is recorded with that church as
 * its parish too (the Impor reading, `../import`).
 */
export const referensiLuarSchema = z.object({
  paroki: requiredTextSchema,
  nomor_surat: optionalTextSchema,
  gereja: optionalTextSchema,
  jenis_baptis: z.enum(JENIS_BAPTIS).nullish(),
  jenis_perkawinan: z.enum(JENIS_PERKAWINAN_LUAR).nullish(),
  buku: optionalTextSchema,
  halaman: positiveIntSchema.nullish(),
  /** Text: books write "45a" as often as "45". */
  nomor: optionalTextSchema,
  tanggal: partialDateSchema.nullish(),
  tempat: optionalTextSchema,
  pelayan: optionalTextSchema,
  wali: optionalTextSchema,
  saksi: optionalTextSchema,
})
export const referensiSakramenLuarSchema = z.object({
  baptis: referensiLuarSchema.nullish(),
  komuni: referensiLuarSchema.nullish(),
  krisma: referensiLuarSchema.nullish(),
  perkawinan: referensiLuarSchema.nullish(),
})
export type ReferensiSakramenLuar = z.infer<typeof referensiSakramenLuarSchema>

export const keterampilanSchema = z.array(requiredTextSchema)

/**
 * What a mutasi request says about its subject (spec 8.7, section 12): the destination
 * parish reads it before any row of the family has reached its devices.
 */
export const ringkasanMutasiSchema = z.object({
  /** The head of the family, or the person (a family without a head: its parish number). */
  nama: requiredTextSchema,
  /**
   * The family's parish number, never its government one (release 18 review M-8: a request
   * carries no government number before Terima; it reaches the destination with the accepted
   * family's rows only). On one line with *No. KK Disdukcapil belum tersedia* when it is the
   * family's shown number.
   */
  nomorKk: optionalTextSchema,
  /**
   * Whether the family has a government KK number (review M-2): Terima says it keeps it, or that
   * the family gets the destination's parish number. Absent from a summary an app before this
   * fix wrote: the destination then reads `nomorKk`.
   */
  kkPemerintah: z.boolean().optional(),
  lingkungan: optionalTextSchema,
  stasi: optionalTextSchema,
  jumlahAnggota: nonNegativeIntSchema,
  jumlahDokumen: nonNegativeIntSchema,
})
export type RingkasanMutasi = z.infer<typeof ringkasanMutasiSchema>

export const SEGMEN_NOMOR = [
  'urutan',
  'kode_paroki',
  'kode_jenis',
  'bulan_romawi',
  'tahun',
  'teks',
] as const

/** `pengaturan_paroki.pola_nomor_surat` (spec 12.2). */
export const polaNomorSuratSchema = z.object({
  segmen: z
    .array(
      z.object({
        jenis: z.enum(SEGMEN_NOMOR),
        /** Only for `teks`. */
        teks: optionalTextSchema,
        /** Zero padding for `urutan`, e.g. 3 gives 041. */
        lebar: positiveIntSchema.nullish(),
      }),
    )
    .min(1),
  pemisah: z.string().min(1).max(3),
  /** One sequence for every letter type, or one per type. */
  urutanBersama: z.boolean(),
  kodeJenis: z.partialRecord(z.enum(JENIS_SURAT), z.string().trim().min(1)),
})
export type PolaNomorSurat = z.infer<typeof polaNomorSuratSchema>

/** The spec default: `<urutan>/<KODE_PAROKI>/<JENIS>/<bulan romawi>/<tahun>`. */
export const POLA_NOMOR_SURAT_BAWAAN: PolaNomorSurat = {
  segmen: [
    { jenis: 'urutan', lebar: 3 },
    { jenis: 'kode_paroki' },
    { jenis: 'kode_jenis' },
    { jenis: 'bulan_romawi' },
    { jenis: 'tahun' },
  ],
  pemisah: '/',
  urutanBersama: false,
  // Only SB is attested (CONTENT.md, "041/KSA/SB/IX/2026"); the rest are editable defaults.
  kodeJenis: {
    surat_baptis: 'SB',
    surat_komuni: 'SKP',
    surat_krisma: 'SKr',
    surat_perkawinan: 'SPk',
    surat_kematian: 'SKm',
    surat_pindah: 'SPd',
    surat_pengantar_nikah: 'SPN',
    kartu_keluarga: 'KK',
  },
}

/**
 * `pengaturan_paroki.kop_surat`: up to five letterhead lines (the parish's official kop: the
 * parish, the diocese and three lines of address; owner, 2026-10-02).
 */
export const KOP_SURAT_MAKS = 5
export const kopSuratSchema = z.array(z.string().trim()).min(1).max(KOP_SURAT_MAKS)
/**
 * The lines the app read and wrote before the official kop: its Pengaturan form took the
 * first three of whatever was stored and wrote those three back on any save.
 */
export const KOP_SURAT_MAKS_LAMA = 3
/**
 * The op `schema_version` from which the app reads and writes all five lines. The push
 * refuses an op below it a `kop_surat` over a stored kop longer than `KOP_SURAT_MAKS_LAMA`
 * (`client_too_old`: the device keeps the op and asks for the update; the op keeps its
 * version, so it stays refused after it), and a device not yet reloaded never cuts the
 * official kop to three lines.
 */
export const KOP_LIMA_BARIS_SEJAK = 8

/** The image types a picture kept in a row may be, in the data URL's own spelling. */
export const MIME_GAMBAR = ['image/png', 'image/jpeg'] as const
/** Base64 letters, then at most two `=`; with the length a multiple of 4, padded base64. */
const BASE64 = /^[A-Za-z0-9+/]+={0,2}$/

/** The prefix and the payload; the length is the schema's first check. */
function gambarDataSah(url: string): boolean {
  const awalan = MIME_GAMBAR.map((mime) => `data:${mime};base64,`).find((a) => url.startsWith(a))
  if (awalan === undefined) return false
  const isi = url.slice(awalan.length)
  return isi.length % 4 === 0 && BASE64.test(isi)
}

/**
 * A picture kept in a row as a data URL: `data:image/png;base64,…` or `data:image/jpeg;base64,…`,
 * the prefix exactly so, then plain padded base64, the whole URL at most `maks` characters. Any
 * other type is refused, SVG first (it can carry script). Over the cap stops at `too_big`: the
 * payload of an oversized string is never scanned. An empty string is refused, not read as "no
 * picture": removing one writes null, and a check cannot turn the stored value into null.
 */
function gambarDataSchema(maks: number) {
  return z.string().max(maks, { abort: true }).refine(gambarDataSah)
}

/**
 * `pengaturan_paroki.logo` (spec 2026-10-03 logo paroki, 2.2–2.4): the parish's logo as a
 * picture data URL (`gambarDataSchema`). The device shrinks the image until the whole URL is at
 * most `LOGO_PAROKI_MAKS` characters. The device's save and the push (spec 9.5 step 4) both
 * check it, through the settings row's schema.
 */
export const LOGO_PAROKI_MAKS = 150_000
/** The image types a logo may be (`MIME_GAMBAR`). */
export const MIME_LOGO_PAROKI = MIME_GAMBAR
export const logoParokiSchema = gambarDataSchema(LOGO_PAROKI_MAKS)

/**
 * `pelayan.foto` (owner, 2026-10-03): the photo of a priest or religious as a picture data URL
 * (`gambarDataSchema`), the device shrinking it until the whole URL is at most
 * `FOTO_PELAYAN_MAKS` characters. The person's row checks it (`pelayanSchema`), on the device's
 * save and in the push; only the kinds of `JENIS_BERFOTO` may carry one (`pelayanRowSchema`).
 */
export const FOTO_PELAYAN_MAKS = 80_000
export const fotoPelayanSchema = gambarDataSchema(FOTO_PELAYAN_MAKS)

/**
 * `pengaturan_paroki.tempat_surat` (owner, 2026-10-03): the town a parish's letters are made
 * in, printed before the date ("Agats, 3 Oktober 2026"). Trimmed, 1 to `TEMPAT_SURAT_MAKS`
 * characters, one line. No town is null: the form writes an empty field as null, and the
 * letters then print the diocese's town (`suratStrings.penutup.tempatBawaan`).
 */
export const TEMPAT_SURAT_MAKS = 60
export const tempatSuratSchema = z
  .string()
  .trim()
  .min(1)
  .max(TEMPAT_SURAT_MAKS)
  .refine((t) => !/[\r\n]/.test(t))

/** `pengaturan_paroki.baris_per_halaman`: rows per page per register book (default 10). */
export const barisPerHalamanSchema = z.partialRecord(
  z.enum(TABEL_REGISTER),
  z.number().int().min(1).max(100),
)
export const BARIS_PER_HALAMAN_BAWAAN = 10
